import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { agentExecutionClient } from '@terra/shared-rest-clients-agent-execution';

type UseAgentExecutionHistoryProps = {
  executionId: string;
  enabled?: boolean;
};

export const AGENT_EXECUTION_HISTORY_QUERY_KEY = 'agent-execution-history';

export const useInvalidateAgentExecutionHistory = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (executionId?: string | null) => {
      const queryKey = executionId
        ? [AGENT_EXECUTION_HISTORY_QUERY_KEY, executionId]
        : [AGENT_EXECUTION_HISTORY_QUERY_KEY];

      await queryClient.invalidateQueries({
        queryKey,
        exact: false,
      });
    },
    [queryClient],
  );
};

export function useAgentExecutionHistory({
  executionId,
  enabled = true,
}: UseAgentExecutionHistoryProps) {
  const invalidateById = useInvalidateAgentExecutionHistory();

  const result = useQuery({
    queryKey: [AGENT_EXECUTION_HISTORY_QUERY_KEY, executionId],
    queryFn: async () => {
      if (!executionId) {
        throw new Error('Execution ID is required');
      }

      const response = await agentExecutionClient.getHistory({
        params: { executionId },
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch agent execution history');
      }

      return response.body;
    },
    enabled: enabled && !!executionId,
    staleTime: 30000,
  });

  const invalidate = useCallback(() => {
    return invalidateById(executionId);
  }, [executionId, invalidateById]);

  return {
    ...result,
    invalidate,
  };
}
